import { useEffect, useState } from "react";
import { ShoppingCart, XCircle, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getMyOrders, returnOrder } from "../api";

function MyOrders() {
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");

  const [returnForm, setReturnForm] = useState({
    reason: "",
    description: "",
  });
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await getMyOrders();

      if (res.data.success) {
        setOrders(res.data.orders);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleReorder = (order) => {
    console.log("Reorder Order:", order);

    // Add products to cart here

    navigate("/cart");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-700";

      case "Processing":
        return "bg-blue-100 text-blue-700";

      case "Shipped":
        return "bg-purple-100 text-purple-700";

      case "Delivered":
        return "bg-green-100 text-green-700";

      case "Completed":
        return "bg-green-100 text-green-700";

      case "Cancelled":
        return "bg-red-100 text-red-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-700";

      case "Pending":
        return "bg-yellow-100 text-yellow-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleReturn = async () => {
    if (!returnForm.reason) {
      alert("Please select a reason.");
      return;
    }

    console.log("Order ID:", selectedOrderId);
    console.log("Return Data:", returnForm);

    try {
      const { data } = await returnOrder(selectedOrderId, returnForm);

      alert(data.message);

      setShowReturnModal(false);
      setReturnForm({
        reason: "",
        description: "",
      });

      fetchOrders();
    } catch (error) {
      console.log("ERROR:", error.response?.data);
      alert(error.response?.data?.message || "Return request failed");
    }
  };
  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-blue-100 p-3 rounded-xl">
            <ShoppingCart className="text-blue-600" />
          </div>

          <div>
            <h1 className="text-3xl font-bold">My Orders</h1>

            <p className="text-gray-500">Total Orders: {orders.length}</p>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {orders.length === 0 && (
        <div className="bg-white rounded-3xl shadow-sm p-16 text-center">
          <ShoppingCart size={60} className="mx-auto text-gray-300 mb-4" />

          <h2 className="text-2xl font-bold mb-2">No Orders Found</h2>

          <p className="text-gray-500">You haven't placed any orders yet.</p>
        </div>
      )}

      {/* Orders */}
      <div className="space-y-5">
        {orders.map((order) => (
          <div
            key={order._id}
            className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition"
          >
            {/* Top */}
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div>
                <h2 className="text-lg font-bold">{order.orderId}</h2>

                <p className="text-sm text-gray-500">
                  Ordered on {new Date(order.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-2 flex-wrap">
                {/* Order Status */}
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                    order.status,
                  )}`}
                >
                  Order: {order.status}
                </span>

                {/* Return Status */}
                {order.returnStatus && (
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      order.returnStatus === "Pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : order.returnStatus === "Approved"
                          ? "bg-green-100 text-green-700"
                          : order.returnStatus === "Rejected"
                            ? "bg-red-100 text-red-700"
                            : order.returnStatus === "Pickup Scheduled"
                              ? "bg-blue-100 text-blue-700"
                              : order.returnStatus === "Received"
                                ? "bg-purple-100 text-purple-700"
                                : order.returnStatus === "Refunded"
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Return: {order.returnStatus}
                  </span>
                )}

                {/* Payment Status */}
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${getPaymentColor(
                    order.paymentStatus,
                  )}`}
                >
                  Payment: {order.paymentStatus}
                </span>
              </div>
            </div>

            {/* Products */}
            <div className="mt-5">
              <h3 className="font-semibold mb-2">Products</h3>

              <div className="space-y-2">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>• {item.title}</span>

                    <span>
                      Qty:
                      {item.quantity || 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div>
                <p className="text-gray-500 text-sm">Amount</p>

                <p className="font-bold text-lg">
                  ₹{order.amount?.toLocaleString()}
                </p>
              </div>

              <div>
                <p className="text-gray-500 text-sm">Items</p>

                <p className="font-semibold">{order.items?.length}</p>
              </div>

              <div>
                <p className="text-gray-500 text-sm">Payment Method</p>

                <p className="font-semibold">{order.paymentMethod}</p>
              </div>

              <div>
                <p className="text-gray-500 text-sm">Order Status</p>
                <p className="font-semibold">{order.status}</p>
              </div>

              <div>
                <p className="text-gray-500 text-sm">Return Status</p>
                <p className="font-semibold text-orange-600">
                  {order.returnStatus || "Not Requested"}
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Ordered</span>
                <span>Processing</span>
                <span>Shipped</span>
                <span>Delivered</span>
              </div>

              <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                <div
                  className={`h-2 rounded-full ${
                    order.status === "Pending"
                      ? "w-1/4 bg-yellow-500"
                      : order.status === "Processing"
                        ? "w-2/4 bg-blue-500"
                        : order.status === "Shipped"
                          ? "w-3/4 bg-purple-500"
                          : order.status === "Delivered" ||
                              order.status === "Completed"
                            ? "w-full bg-green-500"
                            : "w-full bg-red-500"
                  }`}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={() => navigate(`/buyer/orders/${order.orderId}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                View
              </button>

              {/* Return Product Button */}
              {order.status === "Delivered" && !order.returnStatus && (
                <button
                  onClick={() => {
                    setSelectedOrderId(order.orderId);
                    setShowReturnModal(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl flex items-center gap-2"
                >
                  <RotateCcw size={18} />
                  Return Product
                </button>
              )}

              {/* Return Status */}
              {order.returnStatus && (
                <button
                  className={`px-5 py-2.5 rounded-xl text-white ${
                    order.returnStatus === "Pending"
                      ? "bg-yellow-500"
                      : order.returnStatus === "Approved"
                        ? "bg-green-600"
                        : order.returnStatus === "Rejected"
                          ? "bg-red-600"
                          : order.returnStatus === "Pickup Scheduled"
                            ? "bg-blue-600"
                            : order.returnStatus === "Received"
                              ? "bg-purple-600"
                              : order.returnStatus === "Refunded"
                                ? "bg-orange-600"
                                : "bg-gray-600"
                  }`}
                >
                  {order.returnStatus}
                </button>
              )}

              {["Delivered", "Completed", "Cancelled"].includes(
                order.status,
              ) && (
                <button
                  onClick={() => handleReorder(order)}
                  className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2"
                >
                  <RotateCcw size={18} />
                  Reorder
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {showReturnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-xl font-bold">Return Product</h2>

              <button
                onClick={() => setShowReturnModal(false)}
                className="text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block mb-2 font-medium">Return Reason</label>

                <select
                  className="w-full border rounded-lg p-3"
                  value={returnForm.reason}
                  onChange={(e) =>
                    setReturnForm({
                      ...returnForm,
                      reason: e.target.value,
                    })
                  }
                >
                  <option value="">Select Reason</option>

                  <option>Damaged Product</option>

                  <option>Wrong Product</option>

                  <option>Missing Parts</option>

                  <option>Quality Issue</option>

                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 font-medium">Description</label>

                <textarea
                  rows={4}
                  className="w-full border rounded-lg p-3"
                  placeholder="Write your issue..."
                  value={returnForm.description}
                  onChange={(e) =>
                    setReturnForm({
                      ...returnForm,
                      description: e.target.value,
                    })
                  }
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="px-5 py-2 rounded-lg border"
                >
                  Cancel
                </button>

                <button
                  onClick={handleReturn}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyOrders;
